import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, NOTIFICATION_CATEGORIES } from '../../contexts/NotificationContext';
import { BackOfficeNotificationItem, NotificationCategory } from '../../domain/payload/response/backOfficeNotification/BackOfficeNotificationResp';
import '../../styles/notification.scss';

// Direct color mapping (avoids Tailwind dependency)
const CATEGORY_COLORS: Record<string, { bg: string; color: string }> = {
  MONITORING: { bg: '#dbeafe', color: '#2563eb' },
  ALERT: { bg: '#fef3c7', color: '#d97706' },
  SYSTEM: { bg: '#f3e8ff', color: '#7c3aed' },
  CAMPAIGN: { bg: '#fce7f3', color: '#db2777' },
  WELCOME: { bg: '#d1fae5', color: '#059669' },
  ANNOUNCEMENT: { bg: '#cffafe', color: '#0891b2' },
};

const Notification: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const {
    notifications,
    unreadCount,
    total,
    totalPages,
    loading,
    lastNewNotificationAt,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    formatRelativeTime,
  } = useNotifications();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch on page/filter/search change, or when new notification arrives
  useEffect(() => {
    fetchNotifications({
      page: currentPage,
      size: pageSize,
      isRead: filter === 'unread' ? false : undefined,
      search: debouncedSearch || undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filter, debouncedSearch, lastNewNotificationAt]);

  const handleFilterChange = (newFilter: 'all' | 'unread') => {
    setFilter(newFilter);
    setCurrentPage(1);
  };

  const handleOpenPage = useCallback(
    (notification: BackOfficeNotificationItem) => {
      if (!notification.isRead) {
        markAsRead(notification.id);
      }
      if (notification.actionValue) {
        navigate(notification.actionValue);
      }
    },
    [markAsRead, navigate]
  );

  const handleMarkAsRead = useCallback(
    (notification: BackOfficeNotificationItem) => {
      if (!notification.isRead) {
        markAsRead(notification.id);
      }
    },
    [markAsRead]
  );

  const handleMarkAllAsRead = useCallback(async () => {
    await markAllAsRead();
    await fetchUnreadCount();
  }, [markAllAsRead, fetchUnreadCount]);

  const getCategoryStyle = (category: string) => {
    return CATEGORY_COLORS[category] || CATEGORY_COLORS.MONITORING;
  };

  const getCategoryIcon = (category: string) => {
    const meta = NOTIFICATION_CATEGORIES[category as NotificationCategory];
    return meta?.icon || 'fa-solid fa-bell';
  };

  return (
    <div className="notif-page-container">
      {/* Header */}
      <h4 className="notif-page-title">Notifications</h4>

      {/* Controls */}
      <div className="notif-page-controls">
        <div className="notif-page-tabs">
          <button
            className={`notif-page-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => handleFilterChange('all')}
          >
            All Notifications
            <span className="notif-page-tab-count">{total}</span>
          </button>
          <button
            className={`notif-page-tab ${filter === 'unread' ? 'active' : ''}`}
            onClick={() => handleFilterChange('unread')}
          >
            Unread
            <span className="notif-page-tab-count">{unreadCount}</span>
          </button>
        </div>
        <div className="notif-page-actions">
          <div className="notif-search-wrapper">
            <i className="fa-solid fa-magnifying-glass" />
            <input
              type="text"
              className="notif-search-input"
              placeholder="Search notifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {unreadCount > 0 && (
            <button
              className="notif-page-mark-all-btn"
              onClick={handleMarkAllAsRead}
            >
              <i className="fa-solid fa-check-double" /> Mark all as read
            </button>
          )}
        </div>
      </div>

      {/* Notification List */}
      <div className="notif-page-list">
        {loading ? (
          <div className="notif-page-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : notifications.length > 0 ? (
          notifications.map((n) => {
            const catStyle = getCategoryStyle(n.category);
            const catIcon = getCategoryIcon(n.category);
            return (
              <div
                key={n.id}
                className={`notif-page-item ${!n.isRead ? 'unread' : ''}`}
              >
                <div
                  className="notif-page-icon"
                  style={{ backgroundColor: catStyle.bg }}
                >
                  <i className={catIcon} style={{ color: catStyle.color }} />
                </div>
                <div className="notif-page-content">
                  <div className="notif-page-item-title">{n.title}</div>
                  <div className="notif-page-item-message">{n.message}</div>
                  <div className="notif-page-item-actions">
                    {n.actionValue && (
                      <button
                        className="notif-open-page-btn"
                        onClick={() => handleOpenPage(n)}
                      >
                        Open Page{' '}
                        <i className="fa-solid fa-up-right-from-square" />
                      </button>
                    )}
                    {!n.isRead && (
                      <button
                        className="notif-mark-read-btn"
                        onClick={() => handleMarkAsRead(n)}
                      >
                        <i className="fa-solid fa-check" /> Mark as read
                      </button>
                    )}
                  </div>
                </div>
                <div className="notif-page-item-time">
                  <i className="fa-regular fa-clock" />{' '}
                  {n.timeAgo || formatRelativeTime(n.createdAt)}
                </div>
              </div>
            );
          })
        ) : (
          <div className="notif-page-empty">
            <i className="fa-regular fa-bell-slash" />
            <span>No notifications found</span>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="notif-page-pagination">
          <button
            className="notif-page-btn"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            <i className="fa-solid fa-chevron-left" /> Previous
          </button>
          <span className="notif-page-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="notif-page-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next <i className="fa-solid fa-chevron-right" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Notification;
